import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/components/AuthProvider";

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();

  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: ""
  });


  useEffect(() => {
    // If user is already authenticated, redirect them
    if (!authLoading && user) {
      redirectAuthenticatedUser();
    }
  }, [user, authLoading, navigate]);

  const redirectAuthenticatedUser = async () => {
    if (!user) return;

    try {
      // Get user role from profiles
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('role')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        // Default redirect to dashboard
        navigate("/");
        return;
      }

      // Check if there was a previous location to redirect to
      const from = location.state?.from?.pathname || "/";
      
      // Role-based default redirects (only if coming from auth directly)
      if (from === "/" || from === "/auth") {
        switch (profile?.role) {
          case 'admin':
            navigate("/");
            break;
          case 'sales':
            navigate("/clients");
            break;
          case 'research':
            navigate("/people");
            break;
          default:
            navigate("/");
        }
      } else {
        // Redirect to the page they were trying to access
        navigate(from);
      }
    } catch (error) {
      console.error('Error during redirect:', error);
      navigate("/");
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    await doLogin();
  };

  const doLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginForm.email,
        password: loginForm.password,
      });

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          setError("Invalid email or password. Please check your credentials.");
        } else {
          setError(error.message);
        }
        return;
      }

      // Update last login (best-effort)
      void supabase
        .from('profiles')
        .update({ last_login: new Date().toISOString() })
        .eq('email', loginForm.email);

      navigate("/");
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) {
        setError(error.message);
        return;
      }

      toast({
        title: "Reset email sent!",
        description: "Check your email for password reset instructions.",
      });

      setShowForgotPassword(false);
      setResetEmail("");
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setResetLoading(false);
    }
  };

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-primary/10 p-4 z-[2147483647] pointer-events-auto" role="dialog" aria-modal="true">
      <Card className="w-full max-w-md relative z-50 pointer-events-auto">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center">
              <img 
                src="/lovable-uploads/d11ad2b7-53d8-4cca-ab99-643fb1a5896e.png" 
                alt="Company Logo" 
                className="w-10 h-10 object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Braincandy</h1>
              <p className="text-sm text-muted-foreground">NEXUS</p>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
              <form ref={formRef} onSubmit={handleLogin} className="space-y-4 pointer-events-auto">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="your@email.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    autoComplete="email"
                    autoFocus
                    className="pointer-events-auto"
                  />
                </div>

                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      autoComplete="current-password"
                      className="pr-10 pointer-events-auto"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent z-20 pointer-events-auto"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={isLoading} onClick={(e) => { e.preventDefault(); doLogin(); }}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={() => { setShowForgotPassword(true); }}
                  disabled={isLoading}
                  className="text-sm text-muted-foreground hover:text-primary"
                >
                  Forgot your password?
                </Button>
              </div>

              {showForgotPassword && (
                <div className="mt-4 p-4 border rounded-lg bg-muted/30">
                  <h3 className="text-sm font-medium mb-3">Reset Password</h3>
                  <form onSubmit={handleForgotPassword} className="space-y-3">
                    <div>
                      <Label htmlFor="resetEmail" className="text-xs">Email Address</Label>
                      <Input
                        id="resetEmail"
                        type="email"
                        placeholder="Enter your email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                        disabled={resetLoading}
                        className="text-sm"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        size="sm"
                        disabled={resetLoading}
                        className="flex-1"
                      >
                        {resetLoading ? (
                          <>
                            <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          "Send Reset Email"
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowForgotPassword(false);
                          setResetEmail("");
                          setError(null);
                        }}
                        disabled={resetLoading}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                </div>
              )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}